import {
  Badge,
  Box,
  Checkbox,
  FormControl,
  FormGroup,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
} from '@mui/material';
import CustomAlert from 'components/shared/CustomAlert/CustomAlert';
import CustomButton from 'components/shared/CustomButton/CustomButton';
import CustomPopOver from 'components/shared/CustomPopOver/CustomPopOver';
import CustomSearch from 'components/shared/CustomSearch/CustomSearch';
import CustomTab from 'components/shared/CustomTab/CustomTab';
import { LightTooltip } from 'components/shared/CustomTooltip/CustomTooltip';
import { initialAlertState, useAlertStatus } from 'customHook/useAlertStatus';
import { Contact } from 'model/contacts.model';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  fetchAllContacts,
  fetchContactById,
  fetchContactDropdowns,
} from 'store/slices/contactSlice';
import zeplinColor from 'theme/Color';
import AddContacts from './AddContacts';
import ContactsGrid from './ContactsGrid';
import style from './ContactsMain.module.css';
import { ContactsTable } from './ContactsTable';
import ContactsView from './ContactsView/ContactsView';

const ContactsMain = () => {
  const location = useLocation();
  const globalSearchItemId = location.state?.globalSearchItemId;
  const dispatch = useDispatch<any>();
  const contactsState = useSelector((state: any) => state.contacts);
  const { alertState, startOperation, completeOperation, getAlertContent, setAlertState } =
    useAlertStatus();
  const [value, setValue] = useState(0);
  const [layout, setLayout] = useState<'column' | 'row'>('column');
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [addContactOpen, setAddContactOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const [sortBy, setSortBy] = useState<string[]>([]);
  const [sortOrder, setSortOrder] = useState<('asc' | 'desc')[]>([]);
  const [drawerState, setDrawerState] = useState(false);
  const [selectedData, setSelectedData] = useState<any | null>(null);
  const [selectedFilters, setSelectedFilters] = useState<{
    status: string[];
    company: string[];
  }>({
    status: [],
    company: [],
  });

  useEffect(() => {
    dispatch(fetchContactDropdowns());
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      fetchAllContacts({
        mine: value === 0,
        params: {
          page: currentPage + 1,
          limit: rowsPerPage,
          search: searchTerm || undefined,
          sortBy: sortBy?.[0] || undefined,
          sortOrder: sortOrder?.[0] || undefined,
          status: selectedFilters?.status?.[0] || undefined,
          company: selectedFilters?.company?.[0] || undefined,
        },
      }),
    );
  }, [dispatch, value, currentPage, rowsPerPage, searchTerm, sortBy, sortOrder]);

  const contactsData = contactsState.contacts || [];
  const contactsTotalCount = contactsState?.total || 0;
  const contactsLoading = Boolean(contactsState?.isLoading);
  const notesData = contactsState?.notes || [];
  const companyOptions = contactsState?.companyOptions?.zoomInfoTargets || [];
  const statusOptions = ['active', 'inactive'];

  useEffect(() => {
    const fetchContact = async () => {
      if (globalSearchItemId) {
        const response = await dispatch(fetchContactById(globalSearchItemId)).unwrap();
        setSelectedData(response.data);
        setDrawerState(true);
      }
    };

    fetchContact();
  }, [globalSearchItemId, dispatch]);

  const onViewDetails = (contact: Contact) => {
    setSelectedData(contact);
    setDrawerState(true);
  };

  const ContactTabs = [
    {
      label: 'My Contacts',
      id: 1,
      isHidden: value !== 0,
      Component: (
        <Box>
          {layout === 'column' ? (
            <ContactsGrid
              onViewDetails={onViewDetails}
              data={contactsData}
              onDelete={(contact, alertText) => {
                completeOperation(contact, alertText || '');
                refetchContacts();
              }}
              onEdit={(contact: any) => {
                setSelectedData(contact);
                setAddContactOpen(true);
              }}
            />
          ) : (
            <ContactsTable
              contacts={contactsData}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalCount={contactsData.length}
              isLoading={false}
              onSort={(field, order) => {
                setSortBy([field]);
                setSortOrder([order]);
              }}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onViewDetails={onViewDetails}
              onDelete={(contact, alertText) => {
                completeOperation(contact, alertText || '');
                refetchContacts();
              }}
              onEdit={(contact: any) => {
                setSelectedData(contact);
                setAddContactOpen(true);
              }}
            />
          )}
        </Box>
      ),
    },
    {
      label: 'All Contacts',
      id: 2,
      isHidden: value !== 1,
      Component: (
        <Box>
          {layout === 'column' ? (
            <ContactsGrid
              onViewDetails={onViewDetails}
              data={contactsData}
              onDelete={(contact, alertText) => {
                completeOperation(contact, alertText || '');
                refetchContacts();
              }}
              onEdit={(contact: any) => {
                setSelectedData(contact);
                setAddContactOpen(true);
              }}
            />
          ) : (
            <ContactsTable
              contacts={contactsData}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalCount={contactsTotalCount}
              isLoading={contactsLoading}
              onSort={(field, order) => {
                setSortBy([field]);
                setSortOrder([order]);
              }}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onViewDetails={onViewDetails}
              onDelete={(contact, alertText) => {
                completeOperation(contact, alertText || '');
                refetchContacts();
              }}
              onEdit={(contact: any) => {
                setSelectedData(contact);
                setAddContactOpen(true);
              }}
            />
          )}
        </Box>
      ),
    },
  ];
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };
  const handleSearchUsers = (searchTerm: string) => {
    setSearchTerm(searchTerm);
  };
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleContactCreateClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAddContactOpen(true);
  };
  const handleFilterChange = (filterType: 'status' | 'company', value: string | string[]) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [filterType]: Array.isArray(value) ? value : [value],
    }));
  };
  const handleClearAllFilters = () => {
    setSelectedFilters({
      status: [],
      company: [],
    });

    setAnchorEl(null);
    dispatch(
      fetchAllContacts({
        mine: value === 0,
        params: {
          page: currentPage + 1,
          limit: rowsPerPage,
          search: searchTerm || undefined,
          sortBy: sortBy?.[0] || undefined,
          sortOrder: sortOrder?.[0] || undefined,
          status: selectedFilters?.status?.[0] || undefined,
          company: selectedFilters?.company?.[0] || undefined,
        },
      }),
    );
  };
  const applyFilter = () => {
    setAnchorEl(null);
    dispatch(
      fetchAllContacts({
        mine: value === 0,
        params: {
          page: currentPage + 1,
          limit: rowsPerPage,
          search: searchTerm || undefined,
          sortBy: sortBy?.[0] || undefined,
          sortOrder: sortOrder?.[0] || undefined,
          status: selectedFilters?.status?.[0] || undefined,
          company: selectedFilters?.company?.[0] || undefined,
        },
      }),
    );
  };
  const refetchContacts = () => {
    dispatch(
      fetchAllContacts({
        mine: value === 0,
        params: {
          page: currentPage + 1,
          limit: rowsPerPage,
          search: searchTerm || undefined,
          sortBy: sortBy?.[0] || undefined,
          sortOrder: sortOrder?.[0] || undefined,
          status: selectedFilters?.status?.[0] || undefined,
          company: selectedFilters?.company?.[0] || undefined,
        },
      }),
    );
  };

  const handleAddContactSuccess = () => {
    completeOperation('create', 'Contact added successfully');
    refetchContacts();
  };

  const hasActiveFilters = selectedFilters.status.length > 0 || selectedFilters.company.length > 0;
  return (
    <Box
      sx={{
        padding: { xs: '20px 20px', xl: '24px 24px' },
        display: 'flex',
        justifyContent: 'space-between',
      }}
    >
      <CustomTab tabs={ContactTabs} handleChange={handleChange} value={value} />
      <Box
        sx={{ position: 'absolute', top: '1.5rem', right: '1.5rem', display: 'flex', gap: '12px' }}
      >
        <Box sx={{ flex: 1, width: '30rem' }}>
          <CustomSearch getProjects={handleSearchUsers} />
        </Box>

        <Box sx={{ display: 'flex', gap: '16px' }}>
          <Badge
            badgeContent={0}
            color='primary'
            sx={{ '.MuiBadge-badge': { top: '0.7rem', right: '0.2rem' } }}
          >
            <LightTooltip
              title={
                <Typography variant='body3' color={zeplinColor.Neutral1}>
                  Filter
                </Typography>
              }
              placement='bottom-end'
              style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
              // sx={{ [`& .${tooltipClasses.tooltip}`]: { backgroundColor: zeplinColor.Neutral1 } }}
            >
              <Box
                sx={{
                  borderRadius: '50px',
                  width: { xs: '30px', xl: '50px' },
                  height: { xs: '30px', xl: '50px' },
                  border: `1px solid ${zeplinColor.Neutral6}`,
                  padding: '0px 10px',
                  display: 'flex',
                  gap: 8,
                  justifyContent: 'center',
                  alignItems: 'center',
                  cursor: 'pointer',
                  //   flex: 1,
                }}
                onClick={handleClick}
              >
                <img src={'/assets/filter.svg'} className={style.filterIcon} />
              </Box>
            </LightTooltip>
          </Badge>
          <Box
            sx={{
              borderRadius: '50px',
              border: `1px solid ${zeplinColor.Neutral6}`,
              padding: '6px',
              width: { xs: '82px', xl: '92px' },
              height: { xs: '40px', xl: '48px' },
              display: 'flex',
              justifyContent: 'space-around',
              alignItems: 'center',
              backgroundColor: zeplinColor.Neutral1,
              position: 'relative', // Important for absolute positioning of slider
            }}
          >
            {/* Animated slider background */}
            <Box
              sx={{
                position: 'absolute',
                width: { xs: '35px', xl: '40px' },
                height: { xs: '28px', xl: '36px' },
                backgroundColor: zeplinColor.Brand1,
                opacity: 0.1,
                borderRadius: '40px',
                transition: 'transform 0.3s ease',
                transform:
                  layout === 'column'
                    ? 'translateX(0)'
                    : { xs: 'translateX(35px)', xl: 'translateX(40px)' },
                left: '6px',
              }}
            />

            {/* Column icon */}
            <Box
              onClick={() => setLayout('column')}
              sx={{
                zIndex: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '40px',
                height: '100%',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
            >
              <img
                src={
                  layout === 'column'
                    ? `/assets/selected-col-layout.svg`
                    : `/assets/column-layout-icon.svg`
                }
                className={layout === 'column' ? style['icons'] : style['selected-icons']}
                alt='Column layout'
                style={{
                  transition: 'all 0.2s ease',
                  transform: layout === 'column' ? 'scale(1.1)' : 'scale(1)',
                }}
              />
            </Box>

            {/* Row icon */}
            <Box
              onClick={() => setLayout('row')}
              sx={{
                zIndex: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '40px',
                height: '100%',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
            >
              <img
                src={
                  layout === 'row'
                    ? `/assets/selected-row-layout.svg`
                    : `/assets/row-layout-icon.svg`
                }
                className={layout === 'row' ? style['icons'] : style['selected-icons']}
                alt='Row layout'
                style={{
                  transition: 'all 0.2s ease',
                  transform: layout === 'row' ? 'scale(1.1)' : 'scale(1)',
                }}
              />
            </Box>
          </Box>
          <LightTooltip
            title={
              <Typography variant='responsiveBody3' color={zeplinColor.Neutral1}>
                New Contact
              </Typography>
            }
            placement='bottom-end'
            style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
            // sx={{ [`& .${tooltipClasses.tooltip}`]: { backgroundColor: zeplinColor.Neutral1 } }}
          >
            <CustomButton
              disabled={false}
              onClick={handleContactCreateClick}
              variant='contained'
              color={'primary'}
              buttonVariant='responsiveButtonLarge'
              style={{
                width: 'fit-content',
                borderRadius: { xs: '10px', xl: '14px' },
                height: { xs: 30, xl: 48 },
                padding: { xs: '10px 12px', xl: '14px 16px' },
                '&.Mui-focusVisible': { backgroundColor: '#333CAA' },
              }}
              textStyle={{ color: zeplinColor.Neutral1, width: 'fit-content' }}
              text={'New Contact'}
              iconPosition='start'
              icon={<img src={`/assets/add-white.svg`} className={style['add-icon']} />}
              // tabindex={4}
            />
          </LightTooltip>
          <CustomPopOver
            setAnchorEl={setAnchorEl}
            anchorEl={anchorEl}
            id={'simple-popover'}
            sx={{
              borderRadius: '14px',
              boxShadow: 'rgba(0, 0, 0, 0.15)',
              marginTop: '8px',
              marginRight: '2rem',
              marginLeft: '-4rem',
            }}
          >
            <Box
              sx={{
                // height: { xs: '22rem', xl: '27rem' },
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box
                sx={{
                  padding: '16px',
                  width: { xs: '250px', xl: '283px' },
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '24px',
                  height: '100%',
                  overflow: 'auto',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant='responsiveHeading3' color={zeplinColor.BasicBlack}>
                    Filter
                  </Typography>
                  <Typography
                    variant='responsiveButtonSmall'
                    color={hasActiveFilters ? zeplinColor.Brand1 : zeplinColor.Neutral8}
                    onClick={hasActiveFilters ? handleClearAllFilters : undefined}
                    sx={{
                      cursor: hasActiveFilters ? 'pointer' : 'default',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    Clear All
                  </Typography>
                </Box>

                <FormGroup sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {/* Assignee Filter */}
                  <FormControl sx={{ width: '100%' }}>
                    <InputLabel
                      id='company-filter-label'
                      sx={{
                        fontSize: { xs: '14px', xl: '16px' },
                        '&.Mui-focused': { color: zeplinColor.Neutral9 },
                      }}
                    >
                      Company
                    </InputLabel>
                    <Select
                      labelId='company-filter-label'
                      multiple
                      value={selectedFilters.company}
                      onChange={(e) => handleFilterChange('company', e.target.value)}
                      renderValue={(selected) => (selected as string[]).join(', ')}
                      input={
                        <OutlinedInput
                          sx={{
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderWidth: '1px',
                              color: zeplinColor.Neutral9,
                              borderColor: zeplinColor.Neutral9,
                            },
                            // paddingLeft: '16px',
                            padding: '0px 14px',
                          }}
                          label='Company'
                        />
                      }
                      IconComponent={() => (
                        <img src='/assets/down-arrow.svg' className={style['icon-24']} />
                      )}
                    >
                      {companyOptions?.map((company: any) => (
                        <MenuItem key={company?.Name} value={company?.Name}>
                          <Checkbox checked={selectedFilters.company.indexOf(company?.Name) > -1} />
                          {company?.Name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl sx={{ width: '100%' }}>
                    <InputLabel
                      id='agency-filter-label'
                      sx={{
                        fontSize: { xs: '14px', xl: '16px' },
                        '&.Mui-focused': { color: zeplinColor.Neutral9 },
                      }}
                    >
                      Status
                    </InputLabel>
                    <Select
                      labelId='status-filter-label'
                      multiple
                      value={selectedFilters.status}
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                      renderValue={(selected) => (selected as string[]).join(', ')}
                      input={
                        <OutlinedInput
                          sx={{
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderWidth: '1px',
                              color: zeplinColor.Neutral9,
                              borderColor: zeplinColor.Neutral9,
                            },
                            // paddingLeft: '16px',
                            padding: '0px 14px',
                          }}
                          label='Agency'
                        />
                      }
                      IconComponent={() => (
                        <img src='/assets/down-arrow.svg' className={style['icon-24']} />
                      )}
                    >
                      {statusOptions?.map((status) => (
                        <MenuItem key={status} value={status}>
                          <Checkbox checked={selectedFilters.status.indexOf(status) > -1} />
                          {status}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </FormGroup>

                <Box sx={{ display: 'flex', alignItems: 'flex-end', flex: 1 }}>
                  <CustomButton
                    onClick={applyFilter}
                    variant='contained'
                    disabled={!hasActiveFilters}
                    color={'primary'}
                    style={{
                      width: '100%',
                      borderRadius: '14px',
                      height: { xs: '48px', md: '56px' },
                    }}
                    textStyle={{
                      color: !hasActiveFilters ? zeplinColor.Neutral8 : zeplinColor.Neutral1,
                      fontSize: { xs: '14px', xl: '16px' },
                    }}
                    text={'Apply'}
                  />
                </Box>
              </Box>
            </Box>
          </CustomPopOver>
        </Box>
      </Box>
      <ContactsView
        setDrawerState={setDrawerState}
        drawerState={drawerState}
        selectedData={selectedData}
        setAnchorEl={setAnchorEl}
        setAddContactOpen={setAddContactOpen}
        onDeleteSuccess={() => {
          setSelectedData(null);
          completeOperation('delete', 'Contact deleted successfully');
          refetchContacts();
        }}
      />
      <AddContacts
        key={addContactOpen.toString()}
        drawerState={addContactOpen}
        setDrawerState={setAddContactOpen}
        onSuccess={handleAddContactSuccess}
        selectedData={selectedData}
      />
      <CustomAlert
        open={alertState.status !== 'idle'}
        handleClose={() => setAlertState(initialAlertState)}
        message={getAlertContent(alertState)}
        error={alertState.status === 'error'}
      />
    </Box>
  );
};

export default ContactsMain;
